'use client'

// ---------------------------------------------------------------------------
// Library → prompt-box @mention bridge.
//
// The library IS the reference system — referencing never creates anything on
// the canvas. `@Sarah` attaches her hero still (or rides as an Element on
// models that support them); `@Sarah/asset` attaches that exact still. Tags
// are copyable text: pasting a whole tag resolves through the same candidates.
//
// Identity is the collection/asset ID, never the name (duplicate names are
// allowed — decision 3). Names only display; rows carry ids and a subtitle
// ("Character · 7 variants") that doubles as the duplicate disambiguator.
// Name-based resolution (typed drills, pasted tags) REFUSES to guess when a
// name is ambiguous.
//
// Scope: enabled-for-this-project rows insert directly; the rest of the
// workspace pool surfaces as FROM LIBRARY rows behind an inline confirm
// (decision 1) — enabling never leaves the prompt.
// ---------------------------------------------------------------------------

import { libraryActions, useLibraryStore, getAssetUrl, type LibAsset } from '@/store/libraryStore'
import type { LibraryRef } from '@/types/shared'
import { useBootstrapStore } from '@/lib/multiplayer/bootstrap'
import { emitLibraryRevealRequest } from '@/lib/floatingLeftPanelEvents'
import { isLibraryEnabled } from '@/lib/libraryFlag'
import { formatAttrValue, hasAttrValue } from './propertyValues'

export function currentLibraryProjectId(): string | null {
  return useBootstrapStore.getState().currentProjectId ?? null
}

/**
 * The @ picker's direct-insert scope is what the user ENABLED for this
 * project. No project context or nothing enabled → nothing inserts directly.
 */
function enabledSetForCurrentProject(): Set<string> {
  const projectId = currentLibraryProjectId()
  if (!projectId) return new Set()
  return new Set(useLibraryStore.getState().projectLinks[projectId] ?? [])
}

const norm = (v: string) => v.replace(/\s+/g, '').toLowerCase()

type LibraryCandidateOptions = { includeVideos?: boolean }

/** The copyable tag for a collection name: "@GrandmaRose" (space-stripped so
    the mention suggestion — which ends at whitespace — survives typing it). */
export function libraryTagFor(collectionName: string, assetName?: string): string {
  const token = collectionName.replace(/\s+/g, '')
  return assetName ? `@${token}/${assetName}` : `@${token}`
}

type LibraryBundleAssetBase = {
  assetId: string
  name: string
  /** Immutable Library identity/provenance captured with every inserted asset. */
  libraryRef: LibraryRef
}

export type LibraryCollectionBundleImage = LibraryBundleAssetBase & {
  kind: 'image'
  imageUrl: string
  thumbnailUrl: string
}

export type LibraryCollectionBundleVideo = LibraryBundleAssetBase & {
  kind: 'video'
  videoUrl: string
  thumbnailUrl: string
  width?: number
  height?: number
}

export type LibraryCollectionBundleTextFragment = LibraryBundleAssetBase & {
  kind: 'text'
  textValue: string
}

/**
 * Canonical generation-facing representation of one Library collection.
 *
 * Images are always hero-first, then creation-order (id breaks timestamp
 * ties); videos and fragments use the same stable creation-order. Browser-
 * local videos are deliberately absent because a reference must survive
 * collaboration, draft persistence, and backend submission.
 */
export interface LibraryCollectionBundle {
  collectionId: string
  name: string
  /** Normalized once at the Library boundary so every insertion path agrees. */
  description: string
  images: LibraryCollectionBundleImage[]
  videos: LibraryCollectionBundleVideo[]
  textFragments: LibraryCollectionBundleTextFragment[]
}

type LibraryStateSnapshot = ReturnType<typeof useLibraryStore.getState>
type BundleCollection = LibraryStateSnapshot['collections'][number]

function libraryRefForAsset(collectionId: string, asset: LibAsset): LibraryRef {
  return { collectionId, assetId: asset.id, versionId: asset.currentVersionId }
}

function compareBundleAssets(a: LibAsset, b: LibAsset): number {
  return a.createdAt - b.createdAt || a.id.localeCompare(b.id)
}

function heroFirstImages(images: LibAsset[], heroAssetId: string | null): LibAsset[] {
  const ordered = [...images].sort(compareBundleAssets)
  const heroIndex = ordered.findIndex(asset => asset.id === heroAssetId)
  if (heroIndex <= 0) return ordered
  const [hero] = ordered.splice(heroIndex, 1)
  return hero ? [hero, ...ordered] : ordered
}

function collectionBundleFor(state: LibraryStateSnapshot, collection: BundleCollection): LibraryCollectionBundle {
  const live = state.assets.filter(asset => asset.collectionId === collection.id && !asset.deletedAt)
  const images = heroFirstImages(
    live.filter(asset => asset.kind === 'image' && !asset.src.startsWith('idb:')),
    collection.heroAssetId,
  ).map((asset): LibraryCollectionBundleImage => ({
    kind: 'image',
    assetId: asset.id,
    name: asset.name,
    imageUrl: getAssetUrl(asset, 'large') || getAssetUrl(asset),
    thumbnailUrl: getAssetUrl(asset),
    libraryRef: libraryRefForAsset(collection.id, asset),
  }))
  const videos = live
    .filter(asset => asset.kind === 'video' && /^https?:\/\//i.test(asset.src))
    .sort(compareBundleAssets)
    .map((asset): LibraryCollectionBundleVideo => ({
      kind: 'video',
      assetId: asset.id,
      name: asset.name,
      videoUrl: asset.src,
      thumbnailUrl: getAssetUrl(asset),
      ...(asset.width ? { width: asset.width } : {}),
      ...(asset.height ? { height: asset.height } : {}),
      libraryRef: libraryRefForAsset(collection.id, asset),
    }))
  const textFragments = live
    .filter(asset => asset.kind === 'text' && asset.name.trim())
    .sort(compareBundleAssets)
    .map((asset): LibraryCollectionBundleTextFragment => ({
      kind: 'text',
      assetId: asset.id,
      name: asset.name,
      textValue: asset.textValue ?? '',
      libraryRef: libraryRefForAsset(collection.id, asset),
    }))

  return {
    collectionId: collection.id,
    name: collection.name,
    description: collection.description?.trim() ?? '',
    images,
    videos,
    textFragments,
  }
}

/** Resolve any live collection by stable id, including video-only collections. */
export function resolveLibraryCollectionBundleById(collectionId: string): LibraryCollectionBundle | null {
  const state = useLibraryStore.getState()
  if (!state.hydrated) return null
  const collection = state.collections.find(candidate => candidate.id === collectionId && !candidate.deletedAt)
  return collection ? collectionBundleFor(state, collection) : null
}

/**
 * Resolve an exact, enabled collection name as used by copied `@Name` tags.
 * Whitespace is ignored to match `libraryTagFor`; duplicate names never guess.
 */
export function resolveEnabledLibraryCollectionBundleByName(collectionName: string): LibraryCollectionBundle | null {
  const state = useLibraryStore.getState()
  if (!state.hydrated) return null
  const needle = norm(collectionName)
  if (!needle) return null
  const enabled = enabledSetForCurrentProject()
  const matches = state.collections.filter(
    collection => !collection.deletedAt && enabled.has(collection.id) && norm(collection.name) === needle,
  )
  return matches.length === 1 ? collectionBundleFor(state, matches[0]) : null
}

export interface LibraryMentionCandidate {
  collectionId: string
  name: string
  thumbnailUrl?: string
  /** Enabled for this project? false = FROM LIBRARY row (inline confirm). */
  enabled: boolean
  /** "Character · 7 variants" — always shown; disambiguates duplicate names. */
  subtitle: string
  /** Identity to stamp on reference slots/elements at insert time. */
  libraryRef: LibraryRef
  /** Media + text fragments — everything a whole-collection insert adds. */
  itemCount: number
}

function candidateFor(
  state: LibraryStateSnapshot,
  collection: BundleCollection,
  enabled: boolean,
  options: LibraryCandidateOptions,
): LibraryMentionCandidate | null {
  const bundle = collectionBundleFor(state, collection)
  const visualAssets = options.includeVideos ? [...bundle.images, ...bundle.videos] : bundle.images
  const primary = visualAssets[0]
  if (!primary || !collection.name.trim()) return null
  const fragmentCount = bundle.textFragments.length
  return {
    collectionId: collection.id,
    name: collection.name,
    thumbnailUrl: primary.thumbnailUrl,
    enabled,
    subtitle: `${visualAssets.length} variant${visualAssets.length === 1 ? '' : 's'}${
      fragmentCount ? ` · ${fragmentCount} text` : ''
    }`,
    libraryRef: primary.libraryRef,
    itemCount: visualAssets.length + fragmentCount,
  }
}

/**
 * Collections offerable in the @ picker. Enabled ones first (IN THIS PROJECT);
 * with `includePool`, the rest of the workspace follows (FROM LIBRARY).
 */
export function listLibraryMentionCandidates(
  includePool = false,
  options: LibraryCandidateOptions = {},
): LibraryMentionCandidate[] {
  const s = useLibraryStore.getState()
  if (!s.hydrated) return []
  const enabled = enabledSetForCurrentProject()
  const out: LibraryMentionCandidate[] = []
  for (const c of s.collections) {
    if (c.deletedAt) continue
    const isEnabled = enabled.has(c.id)
    if (!isEnabled && !includePool) continue
    const cand = candidateFor(s, c, isEnabled, options)
    if (cand) out.push(cand)
  }
  return out.sort((a, b) => Number(b.enabled) - Number(a.enabled))
}

export interface LibraryAssetCandidate {
  assetId: string
  name: string
  kind: 'image' | 'video' | 'text'
  thumbnailUrl: string
  /** Full-size URL to attach as an image reference (image kind only). */
  imageUrl: string
  /** Original video URL submitted as a Reference Video (video kind only). */
  videoUrl?: string
  width?: number
  height?: number
  /** Prompt-fragment body (text kind only). */
  textValue?: string
  /** Property value labels ("Brown", "Sitting") — shown as the row subtitle
      and searched by the post-slash query, so "@Cats/brown" finds the brown cat. */
  valueLabels: string[]
  /** Property-aware search explanations ("Color: Brown"). */
  valueReasons: string[]
  libraryRef: LibraryRef
}

/** Post-slash drill query: every whitespace-separated token must appear in the
    asset's name or one of its property value labels. */
export function assetMatchesQuery(a: Pick<LibraryAssetCandidate, 'name' | 'valueLabels'>, q: string): boolean {
  const hay = [a.name, ...a.valueLabels].join(' ').toLowerCase()
  return q
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every(token => hay.includes(token))
}

export interface RankedLibraryAssetMatch {
  rank: 0 | 1 | 2 | 3
  reason?: string
}

/**
 * Flat @ search ranking: exact asset name → name prefix → name substring →
 * property value. Whitespace is ignored because the mention trigger itself
 * ends at spaces in several editor contexts.
 */
export function rankLibraryAssetMatch(
  asset: Pick<LibraryAssetCandidate, 'name' | 'valueLabels' | 'valueReasons'>,
  query: string,
): RankedLibraryAssetMatch | null {
  const normalize = (value: string) => value.replace(/\s+/g, '').toLowerCase()
  const needle = normalize(query)
  if (!needle) return null
  const name = normalize(asset.name)
  if (name === needle) return { rank: 0 }
  if (name.startsWith(needle)) return { rank: 1 }
  if (name.includes(needle)) return { rank: 2 }
  const propertyIndex = asset.valueLabels.findIndex(value => normalize(value).includes(needle))
  return propertyIndex >= 0 ? { rank: 3, reason: asset.valueReasons[propertyIndex] } : null
}

export interface LibraryDrillResult {
  collectionId: string
  name: string
  assets: LibraryAssetCandidate[]
}

/** Drill-in by ID — the picker rows carry ids, so stepping into a collection
    never re-resolves by name (the duplicate-name bug fix). */
export function listLibraryAssetCandidatesById(
  collectionId: string,
  options: LibraryCandidateOptions = {},
): LibraryDrillResult | null {
  const s = useLibraryStore.getState()
  const bundle = resolveLibraryCollectionBundleById(collectionId)
  if (!bundle) return null
  const liveProps = s.properties.filter(p => !p.deletedAt)
  const valuesFor = (a: LibAsset) =>
    liveProps.flatMap(property => {
      if (!hasAttrValue(a.attrs[property.key])) return []
      const value = formatAttrValue(property, a.attrs[property.key])
      return value ? [{ value, reason: `${property.label}: ${value}` }] : []
    })
  const rawById = new Map(s.assets.map(asset => [asset.id, asset]))
  const images = bundle.images.map((image): LibraryAssetCandidate => {
    const raw = rawById.get(image.assetId)
    const values = raw ? valuesFor(raw) : []
    return {
      assetId: image.assetId,
      name: image.name,
      kind: 'image',
      thumbnailUrl: image.thumbnailUrl,
      imageUrl: image.imageUrl,
      valueLabels: values.map(value => value.value),
      valueReasons: values.map(value => value.reason),
      libraryRef: image.libraryRef,
    }
  })
  const videos = options.includeVideos
    ? bundle.videos.map((video): LibraryAssetCandidate => {
        const raw = rawById.get(video.assetId)
        const values = raw ? valuesFor(raw) : []
        return {
          assetId: video.assetId,
          name: video.name,
          kind: 'video',
          thumbnailUrl: video.thumbnailUrl,
          imageUrl: '',
          videoUrl: video.videoUrl,
          width: video.width,
          height: video.height,
          valueLabels: values.map(value => value.value),
          valueReasons: values.map(value => value.reason),
          libraryRef: video.libraryRef,
        }
      })
    : []
  // Text fragments ride the same drill — picking one inserts a variable chip.
  const fragments = bundle.textFragments.map((fragment): LibraryAssetCandidate => ({
    assetId: fragment.assetId,
    name: fragment.name,
    kind: 'text',
    thumbnailUrl: '',
    imageUrl: '',
    textValue: fragment.textValue,
    valueLabels: [],
    valueReasons: [],
    libraryRef: fragment.libraryRef,
  }))
  const assets = [...images, ...videos, ...fragments]
  if (!assets.length) return null
  return { collectionId: bundle.collectionId, name: bundle.name, assets }
}

/**
 * Drill-in by typed name ("@Name/…"). Resolves against ENABLED collections;
 * refuses to guess when the name is ambiguous (two Sarahs → null, the picker
 * rows with subtitles are the disambiguator).
 */
export function listLibraryAssetCandidates(
  collectionName: string,
  options: LibraryCandidateOptions = {},
): LibraryDrillResult | null {
  const bundle = resolveEnabledLibraryCollectionBundleByName(collectionName)
  return bundle ? listLibraryAssetCandidatesById(bundle.collectionId, options) : null
}

export interface FlatLibraryAssetCandidate extends LibraryAssetCandidate {
  collectionId: string
  collectionName: string
  enabled: boolean
  matchReason?: string
  matchRank: RankedLibraryAssetMatch['rank']
}

/**
 * Cross-collection asset search for the top-level @ picker. Every result keeps
 * its owning collection identity so selection uses the exact same insertion
 * path as collection drill-in and duplicate collection names remain safe.
 */
export function listFlatLibraryAssetCandidates(
  query: string,
  limit = 12,
  options: LibraryCandidateOptions = {},
): FlatLibraryAssetCandidate[] {
  if (!query.trim()) return []
  const s = useLibraryStore.getState()
  if (!s.hydrated) return []
  const enabled = enabledSetForCurrentProject()
  const matches: FlatLibraryAssetCandidate[] = []

  for (const collection of s.collections) {
    if (collection.deletedAt) continue
    const drill = listLibraryAssetCandidatesById(collection.id, options)
    if (!drill) continue
    for (const asset of drill.assets) {
      const match = rankLibraryAssetMatch(asset, query)
      if (!match) continue
      matches.push({
        ...asset,
        collectionId: collection.id,
        collectionName: collection.name,
        enabled: enabled.has(collection.id),
        matchReason: match.reason,
        matchRank: match.rank,
      })
    }
  }

  return matches
    .sort(
      (a, b) =>
        Number(b.enabled) - Number(a.enabled) ||
        a.matchRank - b.matchRank ||
        a.collectionName.localeCompare(b.collectionName) ||
        a.name.localeCompare(b.name),
    )
    .slice(0, limit)
}

// --- Text fragments (kind='text' assets — prompt text riding with a subject) --

export interface LibraryVariableCandidate {
  /** The text asset's id — expansion resolves @{id:name} against it. */
  variableId: string
  name: string
  value: string
  collectionId: string
  collectionName: string
  /** Enabled = its COLLECTION is enabled for this project. */
  enabled: boolean
}

export function listLibraryVariableCandidates(includePool = false): LibraryVariableCandidate[] {
  const s = useLibraryStore.getState()
  if (!s.hydrated) return []
  const enabled = enabledSetForCurrentProject()
  const byId = new Map(s.collections.filter(c => !c.deletedAt).map(c => [c.id, c]))
  const out: LibraryVariableCandidate[] = []
  for (const a of s.assets) {
    if (a.deletedAt || a.kind !== 'text' || !a.name.trim()) continue
    const c = byId.get(a.collectionId)
    if (!c) continue
    const isEnabled = enabled.has(c.id)
    if (!isEnabled && !includePool) continue
    out.push({
      variableId: a.id,
      name: a.name,
      value: a.textValue ?? '',
      collectionId: c.id,
      collectionName: c.name,
      enabled: isEnabled,
    })
  }
  return out.sort((a, b) => Number(b.enabled) - Number(a.enabled))
}

// --- Inline enables (the @-menu confirm — decision 1) --------------------------

/** Enable a collection for the current project. Returns false without project context. */
/** Whether a collection is already enabled for the current project. */
export function isCollectionEnabledForCurrentProject(collectionId: string): boolean {
  return enabledSetForCurrentProject().has(collectionId)
}

export function enableCollectionForCurrentProject(collectionId: string): boolean {
  const projectId = currentLibraryProjectId()
  if (!projectId) return false
  libraryActions.enableCollectionForProject(projectId, collectionId)
  return true
}

/** Undo the last library mutation (backs the "added to project · Undo" toast). */
export function undoLastLibraryAction(): void {
  useLibraryStore.getState().undo()
}

// --- Pasted tags ----------------------------------------------------------------

export type ResolvedLibraryTag =
  | {
      kind: 'collection'
      collectionId: string
      name: string
      /** Canonical mixed-media payload; insertion paths must consume this. */
      bundle: LibraryCollectionBundle
      /** Compatibility hero fields for image-only callers. */
      imageUrl: string
      thumbnailUrl: string
      libraryRef: LibraryRef
    }
  | {
      kind: 'asset'
      collectionId: string
      collectionName: string
      assetName: string
      imageUrl: string
      thumbnailUrl: string
      libraryRef: LibraryRef
    }
  | {
      /** A text fragment: pasting its tag inserts the variable chip, never an image ref. */
      kind: 'fragment'
      collectionId: string
      collectionName: string
      variableId: string
      name: string
    }
  | {
      kind: 'video'
      collectionId: string
      collectionName: string
      assetName: string
      assetId: string
      videoUrl: string
      thumbnailUrl: string
      width?: number
      height?: number
      libraryRef: LibraryRef
    }

/**
 * Resolve a pasted tag ("@Sarah" or "@Sarah/Evening laugh") against the
 * project's enabled collections. Only exact, whole-tag text resolves — and
 * only when the name is unambiguous (never guess between two Sarahs).
 */
export function resolveLibraryTag(text: string, options: LibraryCandidateOptions = {}): ResolvedLibraryTag | null {
  const m = /^@([^\s/]+)(?:\/(.+))?$/.exec(text.trim())
  if (!m) return null
  const bundle = resolveEnabledLibraryCollectionBundleByName(m[1])
  if (!bundle) return null
  if (m[2] !== undefined) {
    const drill = listLibraryAssetCandidatesById(bundle.collectionId, options)
    if (!drill) return null
    const q = m[2].trim().toLowerCase()
    // Name wins (exact, then substring); property values back it up, so a
    // pasted "@Cats/brown" resolves via Color=Brown when no name matches.
    const a =
      drill.assets.find(x => x.name.toLowerCase() === q) ??
      drill.assets.find(x => x.name.toLowerCase().includes(q)) ??
      drill.assets.find(x => assetMatchesQuery(x, q))
    if (!a) return null
    // Text fragments have no imageUrl — returning them as 'asset' would make
    // the editors attach an empty reference image. They paste as variables.
    if (a.kind === 'text') {
      return {
        kind: 'fragment',
        collectionId: drill.collectionId,
        collectionName: drill.name,
        variableId: a.assetId,
        name: a.name,
      }
    }
    if (a.kind === 'video' && a.videoUrl) {
      return {
        kind: 'video',
        collectionId: drill.collectionId,
        collectionName: drill.name,
        assetName: a.name,
        assetId: a.assetId,
        videoUrl: a.videoUrl,
        thumbnailUrl: a.thumbnailUrl,
        width: a.width,
        height: a.height,
        libraryRef: a.libraryRef,
      }
    }
    return {
      kind: 'asset',
      collectionId: drill.collectionId,
      collectionName: drill.name,
      assetName: a.name,
      imageUrl: a.imageUrl,
      thumbnailUrl: a.thumbnailUrl,
      libraryRef: a.libraryRef,
    }
  }
  const hero = bundle.images[0]
  const videoFallback = options.includeVideos ? bundle.videos[0] : undefined
  const primary = hero ?? videoFallback
  if (!primary) return null
  return {
    kind: 'collection',
    collectionId: bundle.collectionId,
    name: bundle.name,
    bundle,
    // Video-aware insertion consumes `bundle.videos`; the poster fallback
    // keeps the legacy image-preview fields total until every caller migrates.
    imageUrl: hero?.imageUrl ?? videoFallback?.thumbnailUrl ?? '',
    thumbnailUrl: primary.thumbnailUrl,
    libraryRef: primary.libraryRef,
  }
}

// --- Chip click → open in Library (click = look) --------------------------------

/**
 * Reverse-resolve a reference URL to its library asset. Image-ref chips carry
 * only the URL captured at insert; library URLs are version-immutable, so a
 * flat-field match covers the current version and the versions table covers
 * refs pinned to an older one (the asset's flat src moved on).
 */
export function findLibraryAssetByUrl(url: string): { collectionId: string; assetId: string } | null {
  if (!url) return null
  const s = useLibraryStore.getState()
  const asset = s.assets.find(a => !a.deletedAt && (a.src === url || a.srcLarge === url))
  if (asset) return { collectionId: asset.collectionId, assetId: asset.id }
  const version = s.versions.find(v => v.src === url || v.srcLarge === url)
  if (!version) return null
  const owner = s.assets.find(a => !a.deletedAt && a.id === version.assetId)
  return owner ? { collectionId: owner.collectionId, assetId: owner.id } : null
}

/**
 * Open an asset in the in-editor Library panel (reveals the panel, focuses
 * the collection, opens the asset's detail). Returns false when the library
 * UI isn't available so callers can fall back to their default click behavior.
 */
export function openLibraryAsset(collectionId: string, assetId?: string): boolean {
  if (!isLibraryEnabled()) return false
  emitLibraryRevealRequest([collectionId], assetId)
  return true
}

// --- Stable element identity ------------------------------------------------------

function hash32Hex(input: string, seed: number): string {
  let hash = 0x811c9dc5 + seed
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }
  return (hash >>> 0).toString(16).padStart(8, '0')
}

/**
 * Deterministic uuid-shaped id for a library-sourced element slot. The same
 * collection (or exact asset) always yields the same id, so @-mentions,
 * rename-sync, dedupe and mention-prune treat library elements exactly like
 * subject elements — and inserting the same collection twice reuses one slot.
 */
export function stableLibraryElementId(libraryRef: LibraryRef | undefined): string | undefined {
  if (!libraryRef?.collectionId) return undefined
  const key = libraryRef.assetId
    ? `library-asset:${libraryRef.collectionId}:${libraryRef.assetId}`
    : `library-collection:${libraryRef.collectionId}`
  const hex = [0, 1, 2, 3].map(seed => hash32Hex(key, seed)).join('')
  const variant = (8 + (Number.parseInt(hex[16] ?? '0', 16) % 4)).toString(16)
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-4${hex.slice(13, 16)}-${variant}${hex.slice(17, 20)}-${hex.slice(20, 32)}`
}

// Reverse index for findLibraryTargetByStableElementId, rebuilt only when the
// library store swaps its collections/assets arrays. Chips call the lookup on
// every render, and hashing every collection×asset pair per call made typing
// hitch on large libraries.
let stableIdIndexCache: {
  collections: unknown
  assets: unknown
  index: Map<string, { collectionId: string; assetId?: string }>
} | null = null

function stableElementIdIndex(): Map<string, { collectionId: string; assetId?: string }> {
  const state = useLibraryStore.getState()
  if (
    stableIdIndexCache &&
    stableIdIndexCache.collections === state.collections &&
    stableIdIndexCache.assets === state.assets
  ) {
    return stableIdIndexCache.index
  }
  const index = new Map<string, { collectionId: string; assetId?: string }>()
  const liveCollectionIds = new Set<string>()
  for (const collection of state.collections) {
    if (collection.deletedAt) continue
    liveCollectionIds.add(collection.id)
    const collectionRef = { collectionId: collection.id }
    const key = stableLibraryElementId(collectionRef)
    if (key) index.set(key, collectionRef)
  }
  for (const asset of state.assets) {
    if (asset.deletedAt || !liveCollectionIds.has(asset.collectionId)) continue
    const assetRef = { collectionId: asset.collectionId, assetId: asset.id }
    const key = stableLibraryElementId(assetRef)
    if (key) index.set(key, assetRef)
  }
  stableIdIndexCache = { collections: state.collections, assets: state.assets, index }
  return index
}

/** Reverse a serialized element ID back to its Library source. This keeps
 * element chips navigable after the editor has been rebuilt from prompt text,
 * where the original `libraryRef` object is no longer present. */
/**
 * Identity for a chip that stands for a WHOLE collection. It must be the
 * collection, not the collection's hero asset — otherwise clicking the chip
 * resolves to one image and the Library panel drills into it, stacking an
 * asset detail under the collection view.
 */
export function stableLibraryCollectionElementId(collectionId: string | undefined): string | undefined {
  return collectionId ? stableLibraryElementId({ collectionId }) : undefined
}

/** True when this slot id points at the given collection, whichever identity
 *  shape it was minted with (older chips used the hero asset's id). */
export function slotIdTargetsLibraryCollection(slotId: string | undefined, collectionId: string): boolean {
  if (!slotId) return false
  return findLibraryTargetByStableElementId(slotId)?.collectionId === collectionId
}

export function findLibraryTargetByStableElementId(
  elementId: string,
): { collectionId: string; assetId?: string } | null {
  if (!elementId) return null
  return stableElementIdIndex().get(elementId) ?? null
}
